/**
 * Wire types for the /memory REST surface and the proposal payload the agent
 * emits via `propose_memory` tool calls.
 *
 * Validated with Zod at the trust boundary (HTTP responses, tool call
 * arguments) so static types are derived rather than declared.
 */

import { z } from 'zod';

export const MEMORY_KINDS = ['directive', 'preference', 'context', 'feedback'] as const;
export const MEMORY_SCOPES = ['session', 'user', 'org'] as const;

export const memoryKindSchema = z.enum(MEMORY_KINDS);
export const memoryScopeSchema = z.enum(MEMORY_SCOPES);

export type MemoryKind = z.infer<typeof memoryKindSchema>;
export type MemoryScope = z.infer<typeof memoryScopeSchema>;

/**
 * The shape returned by the agent's `propose_memory` tool. Lives inside
 * `EpiToolCall.result` for tool calls with `toolName === 'propose_memory'`.
 *
 * `suggested_scope` is restricted to `session | user` on the wire — the
 * agent never suggests `org` (that's the user's call). The card UI may
 * still let the user widen to `org` after confirming.
 */
export const memoryProposalSchema = z.object({
  proposal_id: z.string(),
  kind: memoryKindSchema,
  text: z.string(),
  suggested_scope: z.enum(['session', 'user']),
  rationale: z.string().nullable().optional(),
});

export type MemoryProposal = z.infer<typeof memoryProposalSchema>;

/**
 * The shape returned by the agent's `propose_forget_memory` tool — same
 * proposal envelope but with the target memory id and reason.
 */
export const memoryForgetProposalSchema = z.object({
  proposal_id: z.string(),
  memory_id: z.string(),
  kind: memoryKindSchema,
  text: z.string(),
  scope: memoryScopeSchema,
  reason: z.string(),
});

export type MemoryForgetProposal = z.infer<typeof memoryForgetProposalSchema>;

/**
 * The shape returned by the agent's `save_memory` tool. Unlike
 * `propose_memory`, this represents an entry that has ALREADY been written
 * — `id` is the persisted memory id. The notification UI uses this to
 * offer an undo affordance (DELETE /memory/{id}) without going through a
 * confirm flow.
 *
 * `scope` is restricted to `session | user` on the wire — `save_memory`
 * never writes at `org` scope (those go through `propose_memory`).
 */
export const memorySavedSchema = z.object({
  id: z.string(),
  kind: memoryKindSchema,
  text: z.string(),
  scope: z.enum(['session', 'user']),
});

export type MemorySaved = z.infer<typeof memorySavedSchema>;

// ---------------------------------------------------------------- HTTP

export const memoryEntrySchema = z.object({
  id: z.string(),
  kind: memoryKindSchema,
  text: z.string(),
  scope: memoryScopeSchema,
  scopeKey: z.string(),
  rationale: z.string().nullable().optional(),
  sourceMessageId: z.string().nullable().optional(),
  pinned: z.boolean(),
  useCount: z.number(),
  lastUsedAt: z.string().nullable().optional(),
  tags: z.array(z.string()).default([]),
  createdAt: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
});

export type MemoryEntry = z.infer<typeof memoryEntrySchema>;

export const memoryEntryEnvelopeSchema = z.object({
  data: memoryEntrySchema,
});

export const memoryListEnvelopeSchema = z.object({
  data: z.array(memoryEntrySchema),
});

/**
 * Body of POST /memory — what the chat UI sends on user-confirm.
 *
 * `scopeKey` is intentionally absent: the server resolves it from the
 * verified token (user_id for `user`, organization_id for `org`) or, for
 * `session`, from `sessionId` in the body. This mirrors the
 * IDENTITY_BOUND_SCOPES discipline on the backend.
 */
export interface MemoryConfirmRequest {
  kind: MemoryKind;
  text: string;
  scope: MemoryScope;
  rationale?: string | null;
  sourceMessageId?: string | null;
  tags?: string[];
  sessionId?: string | null;
}
